import type { MatchCompanyConfig } from '@/types/custom-playbooks';

/** The step's switches, as the server saves them (`play_match_company`). */
export type MatchCompanySwitch =
  | 'freshestCopy'
  | 'invalidEmailMeansLeft'
  | 'neverAtAccount';

/** What a fresh Match company step saves; equal to the server's
 * `play_match_company.DEFAULT_CONFIG`. */
export const MATCH_COMPANY_DEFAULT_CONFIG: Required<
  Pick<MatchCompanyConfig, MatchCompanySwitch>
> = {
  freshestCopy: true,
  invalidEmailMeansLeft: false,
  neverAtAccount: false,
};

/** Each switch's label (`play_match_company.SWITCH_LABELS`), in panel order. */
export const MATCH_COMPANY_SWITCHES: readonly [MatchCompanySwitch, string][] = [
  ['freshestCopy', 'Freshest copy decides'],
  ['invalidEmailMeansLeft', 'Invalid work email means left'],
  ['neverAtAccount', 'Never at the account means left'],
];

const KEYS = new Set([
  'freshestCopy',
  'invalidEmailMeansLeft',
  'neverAtAccount',
  'accountLinkedinField',
  'delay',
]);

/** A switch as the server reads it: its saved value, else its default. */
export function matchCompanySwitch(
  config: Partial<MatchCompanyConfig> | undefined,
  key: MatchCompanySwitch
): boolean {
  const value = config?.[key];
  return typeof value === 'boolean' ? value : MATCH_COMPANY_DEFAULT_CONFIG[key];
}

/**
 * Why a Match company step can't turn On yet, mirroring the server's
 * `play_match_company.validate_config`; null when it is complete. That a Job
 * history step comes before it is the server's graph check.
 */
export function matchCompanyConfigError(
  config: Partial<MatchCompanyConfig> | undefined
): string | null {
  if (!config || typeof config !== 'object' || Array.isArray(config))
    return 'Set up Match company.';
  const extra = Object.keys(config)
    .filter((key) => !KEYS.has(key))
    .sort();
  if (extra.length) return `Match company doesn't take ${extra.join(', ')}.`;
  for (const [key, label] of MATCH_COMPANY_SWITCHES) {
    if (key in config && typeof config[key] !== 'boolean')
      return `${label} is on or off.`;
  }
  const field = config.accountLinkedinField;
  if (
    field !== undefined &&
    field !== null &&
    !(
      typeof field === 'string' &&
      field.length > 0 &&
      field.length <= 200 &&
      field === field.trim()
    )
  )
    return 'Pick the field that holds the account’s LinkedIn page.';
  return null;
}

/** The card line, as the server writes it (`play_match_company.step_summary`):
 * "Account against job history · freshest copy". */
export function matchCompanySummary(
  config: Partial<MatchCompanyConfig> | undefined
): string {
  const on = (
    [
      ['freshestCopy', 'freshest copy'],
      ['invalidEmailMeansLeft', 'invalid email means left'],
      ['neverAtAccount', 'never at the account'],
    ] as const
  )
    .filter(([key]) => matchCompanySwitch(config, key))
    .map(([, words]) => words);
  return ['Account against job history', ...on].join(' · ');
}
